import type { DocNode } from '@wehunt/ui'

interface RawDocRecord {
  storagePath?: string
  isFolder?: boolean
  uploaderName?: string | null
  updatedAt?: { toDate(): Date }
}

function formatDate(ts: { toDate(): Date }): string {
  return ts.toDate().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function fileKind(name: string): 'pdf' | 'doc' | 'xls' | 'img' {
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  if (ext === 'pdf') return 'pdf'
  if (ext === 'xls' || ext === 'xlsx') return 'xls'
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'img'
  return 'doc'
}

export function buildDocTree(
  groupId: string,
  records: RawDocRecord[],
): Record<string, DocNode> {
  const tree: Record<string, DocNode> = {
    root: { id: 'root', type: 'folder', name: 'Documents', parent: null, children: [] },
  }

  const prefix = `documents/${groupId}/`

  function ensureFolder(relPath: string, parentId: string): void {
    if (tree[relPath]) return
    const name = relPath.split('/').pop() ?? relPath
    tree[relPath] = { id: relPath, type: 'folder', name, parent: parentId, children: [] }
    const parent = tree[parentId]
    if (parent?.type === 'folder' && !parent.children.includes(relPath)) {
      parent.children.push(relPath)
    }
  }

  for (const rec of records) {
    const sp = rec.storagePath ?? ''
    // storagePath may be a plain relative path ("documents/g1/...") or a gs:// URL
    // ("gs://bucket.appspot.com/documents/g1/..."); indexOf handles both formats.
    const idx = sp.indexOf(prefix)
    if (idx === -1) continue
    const rel = sp.slice(idx + prefix.length)
    if (!rel) continue

    const segments = rel.split('/')

    // Ensure every intermediate folder exists
    for (let i = 0; i < segments.length - 1; i++) {
      const folderRel = segments.slice(0, i + 1).join('/')
      const parentId = i === 0 ? 'root' : segments.slice(0, i).join('/')
      ensureFolder(folderRel, parentId)
    }

    const parentId = segments.length === 1 ? 'root' : segments.slice(0, -1).join('/')
    const name = segments[segments.length - 1]

    if (rec.isFolder) {
      ensureFolder(rel, parentId)
      const node = tree[rel]
      if (node?.type === 'folder') {
        if (rec.updatedAt) node.updated = formatDate(rec.updatedAt)
        // Skip null/empty uploaderName — leave by unset rather than storing empty string
        if (rec.uploaderName) node.by = rec.uploaderName
      }
    } else {
      const parent = tree[parentId]
      tree[rel] = {
        id: rel,
        type: 'file',
        kind: fileKind(name),
        name,
        parent: parentId,
        size: '—',
        updated: rec.updatedAt ? formatDate(rec.updatedAt) : '',
        by: rec.uploaderName ?? '',
      }
      if (parent?.type === 'folder' && !parent.children.includes(rel)) {
        parent.children.push(rel)
      }
    }
  }

  return tree
}
